import { AxiosError } from 'axios'
import { ApiProvider } from '../provider'
import { APIRoutes } from '../routes'
import { InviteUserItem } from 'api/types/auth'

class Auth {
  // login method
  async login({
    email,
    password,
  }: {
    email: string
    password: string
  }): Promise<string | (AxiosError | Error)> {
    return ApiProvider.postRequest(`${APIRoutes.auth.login}`, {
      email,
      password,
    }) as Promise<string | (AxiosError | Error)>
  }

  // change user information method
  async changeUserInformation({
    contactNo,
    name,
    phone,
    birthdate,
    id_card,
    id_card_expiricy,
    hasMFA,
  }: {
    contactNo: number
    name: string | null
    phone: string | null
    birthdate: Date | null
    id_card: string
    id_card_expiricy: Date | null
    hasMFA: boolean
  }): Promise<any | (AxiosError | Error)> {
    const values = {
      contactNo: contactNo,
      name: name?.trim(),
      phone: phone?.trim(),
      birthdate: birthdate,
      idCard: id_card,
      idCardExpiracy: id_card_expiricy,
      hasMFA: hasMFA,
    }
    return ApiProvider.postRequest(
      `${APIRoutes.auth.changeUserInformation}`,
      values,
      false,
    ) as Promise<any | (AxiosError | Error)>
  }

  // change password method
  async changeUserPassword({
    email,
    oldPassword,
    newPassword,
  }: {
    email: string
    oldPassword: string
    newPassword: string
  }): Promise<any | (AxiosError | Error)> {
    return ApiProvider.postRequest(
      `${APIRoutes.auth.changePassword}`,
      {
        email,
        oldPassword,
        newPassword,
      },
      false,
    ) as Promise<any | (AxiosError | Error)>
  }

  // reset password - send email to generate token
  async resetPasswordSendEmailGenerateToken({
    email,
  }: {
    email: string
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.resetPasswordSendEmailGenerateToken}`
    return ApiProvider.postRequest(url, { username: email }, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  // change password method
  async resetPassword({
    email,
    newPassword,
    resetToken,
  }: {
    email: string
    newPassword: string
    resetToken: string
  }): Promise<any | (AxiosError | Error)> {
    return ApiProvider.postRequest(
      `${APIRoutes.auth.resetPassword}`,
      {
        email,
        newPassword,
        resetToken,
      },
      false,
    ) as Promise<any | (AxiosError | Error)>
  }

  // get user invite list
  async getUserInviteList({
    brokerNo,
  }: {
    brokerNo: number
  }): Promise<InviteUserItem[] | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.getUserInviteList}?brokerNo=${brokerNo}`
    return ApiProvider.getRequest(url) as Promise<
      InviteUserItem[] | (AxiosError | Error)
    >
  }

  // generate user invite
  async generateInvite({
    brokerNo,
    email,
  }: {
    brokerNo: number
    email: string
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.generateInvite}`
    const body = {
      brokerNo: brokerNo,
      email: email,
    }
    return ApiProvider.postRequest(url, body, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  async deleteInvite({
    email,
    brokerNo,
  }: {
    email: string
    brokerNo: number
  }): Promise<string | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.deleteInvite}`

    const body = {
      brokerNo: brokerNo,
      email: email,
    }
    return ApiProvider.deleteRequest(url, body) as Promise<
      string | (AxiosError | Error)
    >
  }

  async register({
    password,
    phone,
    invite,
    name,
    nif,
  }: {
    password: string
    phone: string
    invite: string
    name: string
    nif: string
  }): Promise<any | (AxiosError | Error)> {
    return ApiProvider.postRequest(
      `${APIRoutes.auth.register}`,
      {
        password,
        phone,
        invite,
        name,
        nif,
        role: 2,
        status: 1,
      },
      false,
    ) as Promise<any | (AxiosError | Error)>
  }

  // send MFA Code
  async sendMFACode({
    username,
  }: {
    username: string
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.sendMFACode}`
    const body = {
      mfaCode: 0,
      username: username,
    }
    return ApiProvider.postRequest(url, body, false) as Promise<
      any | (AxiosError | Error)
    >
  }

  // validate MFA Code
  async validateMFACode({
    username,
    mfaCode,
  }: {
    username: string
    mfaCode: number
  }): Promise<any | (AxiosError | Error)> {
    const url = `${APIRoutes.auth.validateMFACode}`
    const body = {
      mfaCode: mfaCode,
      username: username,
    }
    return ApiProvider.postRequest(url, body, false) as Promise<
      any | (AxiosError | Error)
    >
  }
}

export const authService = new Auth()
